import { ChartMappingSpec } from '../models/ChatMessage';

// Color palette is chosen entirely client-side; the backend only sends column indexes/names as
// data, never executable code (#1817).
export const COLOR_PALETTE = [
    'rgba(83, 102, 255, 0.6)',
    'rgba(255, 99, 132, 0.6)',
    'rgba(54, 162, 235, 0.6)',
    'rgba(255, 206, 86, 0.6)',
    'rgba(75, 192, 192, 0.6)',
    'rgba(153, 102, 255, 0.6)',
    'rgba(255, 159, 64, 0.6)',
    'rgba(199, 199, 199, 0.6)',
    'rgba(255, 99, 255, 0.6)',
    'rgba(0, 181, 204, 0.6)',
    'rgba(0, 204, 0, 0.6)',
    'rgba(255, 204, 0, 0.6)',
    'rgba(204, 0, 0, 0.6)',
    'rgba(0, 204, 204, 0.6)',
];

/**
 * Fixed transform that shapes dataset rows into a chart.js `data` object: group rows by the label
 * column, sum each selected data column per label, and assign palette colors. This used to arrive
 * from the backend as a JS string and run through `new Function`/`eval`; it is now a deterministic
 * client-side function driven by a declarative spec, so no backend-supplied code ever executes (#1817).
 *
 * Pure and dependency-free so it can be unit-tested directly (the eval'd path it replaces had no
 * test coverage). Mutates `config` in place.
 */
export const applyChartMapping = (
    config: Record<string, unknown>,
    rows: unknown[][],
    spec: ChartMappingSpec,
    chartType: string,
): void => {
    const labelIndex = spec.labelColumnIndex;
    const labels = [...new Set(rows.map((row) => row[labelIndex]))];

    const data = (config.data ??= {}) as Record<string, unknown>;
    data.labels = labels;

    const pieColors = labels.map((_, i) => COLOR_PALETTE[i % COLOR_PALETTE.length]);

    data.datasets = spec.dataColumnIndexes.map((colIndex, i) => {
        const values = labels.map((label) =>
            rows
                .filter((row) => row[labelIndex] === label && row[colIndex] !== undefined)
                .reduce((sum, row) => sum + (Number(row[colIndex]) || 0), 0),
        );

        return {
            label: spec.columnNames[i],
            data: values,
            backgroundColor: chartType === 'pie' ? pieColors : COLOR_PALETTE[i % COLOR_PALETTE.length],
        };
    });
};
